import { Icon } from '@appd/uik';
import { Datasource, ExpressionJson } from '../../../models';

export type ComponentProps = {
  name: string;
  props: Record<string, any>;
};

export type WidgetProps = {
  container?: ComponentProps;
  component: ComponentProps;
};

export type WidgetJson = {
  name?: string;
  title?: string;
  description?: string;
  tags?: unknown[] | (string | { [key: string]: unknown })[];
  spec?: {
    widget?: WidgetProps;
    datasources?: Record<string, Datasource>;
    expressions?: Record<string, unknown>;
    view?: unknown[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type VisualizationEditorTemplate = {
  name: string; // identifier that will be used for editable component in dashboard json
  description?: string;
  version: string;
  author: string;
  owner: string;
  solution: string;
  createdAt: string;
  updatedAt: string;
  spec: {
    props: {
      // 1:1 mapping of editable property to selected value in properties panel
      editable: Record<string, any>;

      // custom component model properties (supports advanced use case of using JSON editor)
      customConfig?: Record<string, any>;
    };

    // default values for editable properties
    editablePropsDefaults: Record<string, any>;

    // 1:n mapping of editable property to model property(ies)
    configMapping?: Record<string, any>;

    // jsonata expression that combines editable properties from props and defaults
    effectiveEditableProps: string;

    // converts editable properties to component model properties using jsonata expressions and configMapping
    editableConfig?: Record<string, any>;

    // component model properties that are constants (defining properties for the component)
    staticConfig?: Record<string, any>;

    // default component model properties
    defaultConfig?: Record<string, any>;

    // jsonata expression that combines config properties (staticConfig, customConfig, editableConfig, defaultConfig)
    effectiveConfig: string;

    view?: unknown[];
  };
  [key: string]: unknown;
};

export const VisualizationType = {
  TIME_SERIES: 'TIME_SERIES',

  HEAT_GRID: 'HEAT_GRID',

  CATEGORICAL_BAR: 'CATEGORICAL_BAR',

  RATIO_BAR: 'RATIO_BAR',

  CATEGORICAL_PIE_FULL: 'CATEGORICAL_PIE_FULL',
  CATEGORICAL_PIE_DONUT: 'CATEGORICAL_PIE_DONUT',

  STATUS_CARD_GROUP: 'STATUS_CARD_GROUP',

  // Health
  HEALTH_DONUT: 'HEALTH_DONUT',
  HEALTH_STACKED_HORIZONTAL_BAR: 'HEALTH_STACKED_HORIZONTAL_BAR',
  HEALTH_HEALTH_CARD: 'HEALTH_HEALTH_CARD',

  // Numeric
  NUMERIC_STATISTIC_CARD: 'NUMERIC_STATISTIC_CARD',

  GRID: 'GRID',

  GAUGE: 'GAUGE',

  // Used for non-editable visualizations
  OTHER: 'OTHER',
} as const;
export type VisualizationTypeOption = (typeof VisualizationType)[keyof typeof VisualizationType];

export type ValidationResult = {
  isValid: boolean;
  errors: string[]; // only used in console for now
};

export interface EditableComponent {
  visualizationType: VisualizationTypeOption;
  name?: string;

  // can consider combining into a display object (used for visualization type selector)
  displayName: string;
  displayIcon: Icon.Type;

  editorTemplate?: VisualizationEditorTemplate;

  propertiesPanel?: (props: VisualizationPropertiesPanelProps) => JSX.Element;

  isValidData?: (obj: any) => ValidationResult;
}

export interface PropertiesPanelContentProps {
  widget: WidgetProps;
  updateWidget: (newWidget: WidgetProps) => void;
}

export interface WidgetPropertiesPanelProps {
  props: Record<string, any>;
  updateProps: (newProps: Record<string, any>) => void;
}

export interface VisualizationPropertiesPanelProps extends WidgetPropertiesPanelProps {
  expressions: string[];
}
